/**
 * Sending a message (unit 26B). `POST /api/chat` answers 202 at once with what
 * became of it; the reply comes on the stream. A dropped connection means the
 * message did not land, so it is sent again — twice, 1.5 s apart — and is
 * safe to: nothing was made.
 */
import { api, type Schemas } from "../api/client";
import type { paths } from "../api/schema";
import { ApiError, ok } from "../api/query";
import { useLayout } from "../shell/useLayout";
import type { Status } from "./fold";
import { useChat } from "./store";

type ChatBody = paths["/api/chat"]["post"]["requestBody"]["content"]["application/json"];
type VisibleWidget = NonNullable<ChatBody["visible_widgets"]>[number];

/**
 * What is on the screen, left to right, as the agent reads it (rule 8). None
 * open is an empty list, and the agent is told so (v1.98). The server checks
 * each tile against its widget's model.
 */
export function visibleWidgets(): VisibleWidget[] {
  return useLayout
    .getState()
    .tiles.flatMap((tile) =>
      tile
        ? [{ widget: tile.widget, tab: tile.tab, params: tile.params } as unknown as VisibleWidget]
        : [],
    );
}

/**
 * This screen's id, made once when it opens. A message comes back on the
 * stream with the id of the screen that sent it: this one drops its own, which
 * it showed as typed, and adds every other screen's.
 */
export const screenId = Math.random().toString(36).slice(2);

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function sendMessage(
  message: string,
  skill: string | null,
  { tries = 3, wait = 1500 }: { tries?: number; wait?: number } = {},
): Promise<Schemas["ChatAccepted"]> {
  const body = {
    message,
    visible_widgets: visibleWidgets(),
    forced_skill: skill,
    screen_id: screenId,
  };
  for (let attempt = 1; ; attempt++) {
    try {
      return await ok(api.POST("/api/chat", { body }));
    } catch (error) {
      if (error instanceof ApiError || attempt >= tries) throw error;
      await pause(wait);
    }
  }
}

/** Why a message did not go, in words. The agent's own errors come on the stream, worded by the server. */
export function whyNotSent(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return "You're offline — check your connection and send it again.";
  }
  return "Can't reach Kindred — the server may be down. Your message wasn't sent.";
}

/** What `POST /api/chat`'s state means for the message (v1.94): waiting never reads as failed. */
function statusOf(state: string): Status {
  if (state === "joined" || state === "queued") return "joined";
  if (state === "answered_confirmation") return "answered";
  return "sent";
}

/**
 * Say `message` in the chat and send it: the strip does this, and so does a
 * widget that asks the agent for something on the person's behalf — Finance's
 * "Check rates now" (29B). The answer arrives in the chat, on the stream.
 */
export async function converse(message: string, skill: string | null = null): Promise<void> {
  const chat = useChat.getState();
  const key = chat.addUser(message);
  chat.start("sending");
  try {
    const answer = await sendMessage(message, skill);
    const status = statusOf(answer.state);
    useChat.getState().mark(key, status);
    if (status === "answered") useChat.getState().settle();
    else useChat.getState().start(status === "joined" ? "joined" : "thinking");
  } catch (e) {
    useChat.getState().settle();
    useChat.getState().mark(key, "failed");
    useChat.getState().say(whyNotSent(e), true);
  }
}
